import { useRef, useEffect, useState } from "react";
import { Undo2, Redo2, Eraser, PaintBucket, Trash2, Brush } from "lucide-react";

const PALETTE = [
  "#000000", "#EF4444", "#F97316", "#FACC15", "#22C55E",
  "#0EA5E9", "#6366F1", "#A855F7", "#EC4899", "#78350F",
  "#FFFFFF", "#94A3B8",
];
const SIZES = [3, 6, 12, 22];

// Reusable HTML5 canvas studio. Exposes export via ref.exportBlob().
export default function DrawingCanvas({ canvasRef }) {
  const ref = useRef(null);
  const [color, setColor] = useState("#000000");
  const [size, setSize] = useState(6);
  const [tool, setTool] = useState("brush"); // brush | eraser | bucket
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const history = useRef([]);
  const redoStack = useRef([]);
  const [, force] = useState(0);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    pushHistory();
    if (canvasRef) {
      canvasRef.current = {
        exportBlob: () => new Promise((res) => c.toBlob(res, "image/png")),
        clear: clearAll,
      };
    }
    // eslint-disable-next-line
  }, []);

  const pushHistory = () => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    history.current.push(ctx.getImageData(0, 0, c.width, c.height));
    if (history.current.length > 30) history.current.shift();
    redoStack.current = [];
    force((n) => n + 1);
  };

  const pos = (e) => {
    const c = ref.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (c.width / rect.width),
      y: (t.clientY - rect.top) * (c.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    const p = pos(e);
    if (tool === "bucket") { floodFill(p.x | 0, p.y | 0, color); pushHistory(); return; }
    drawing.current = true;
    last.current = p;
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = ref.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
    ctx.lineWidth = tool === "eraser" ? size * 2 : size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (drawing.current) { drawing.current = false; pushHistory(); }
  };

  const undo = () => {
    if (history.current.length <= 1) return;
    const c = ref.current; const ctx = c.getContext("2d");
    redoStack.current.push(history.current.pop());
    ctx.putImageData(history.current[history.current.length - 1], 0, 0);
    force((n) => n + 1);
  };
  const redo = () => {
    if (!redoStack.current.length) return;
    const c = ref.current; const ctx = c.getContext("2d");
    const img = redoStack.current.pop();
    ctx.putImageData(img, 0, 0);
    history.current.push(img);
    force((n) => n + 1);
  };
  const clearAll = () => {
    const c = ref.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);
    pushHistory();
  };

  const hexToRgba = (hex) => {
    const h = hex.replace("#", "");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), 255];
  };
  const floodFill = (sx, sy, hex) => {
    const c = ref.current; const ctx = c.getContext("2d");
    const { width: W, height: H } = c;
    const img = ctx.getImageData(0, 0, W, H);
    const d = img.data;
    const idx = (x, y) => (y * W + x) * 4;
    const start = idx(sx, sy);
    const target = [d[start], d[start + 1], d[start + 2], d[start + 3]];
    const fill = hexToRgba(hex);
    if (target.every((v, i) => v === fill[i])) return;
    const stack = [[sx, sy]];
    const near = (i) =>
      Math.abs(d[i] - target[0]) < 24 && Math.abs(d[i + 1] - target[1]) < 24 &&
      Math.abs(d[i + 2] - target[2]) < 24 && Math.abs(d[i + 3] - target[3]) < 24;
    while (stack.length) {
      const [x, y] = stack.pop();
      if (x < 0 || y < 0 || x >= W || y >= H) continue;
      const i = idx(x, y);
      if (!near(i)) continue;
      d[i] = fill[0]; d[i + 1] = fill[1]; d[i + 2] = fill[2]; d[i + 3] = fill[3];
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    ctx.putImageData(img, 0, 0);
  };

  return (
    <div className="space-y-3" data-testid="pl-drawing-canvas">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setTool("brush")}
          className={`nb-btn text-xs ${tool === "brush" ? "" : "nb-btn-ghost"}`}
          data-testid="pl-tool-brush"><Brush size={14} />Brush</button>
        <button type="button" onClick={() => setTool("eraser")}
          className={`nb-btn text-xs ${tool === "eraser" ? "" : "nb-btn-ghost"}`}
          data-testid="pl-tool-eraser"><Eraser size={14} />Eraser</button>
        <button type="button" onClick={() => setTool("bucket")}
          className={`nb-btn text-xs ${tool === "bucket" ? "" : "nb-btn-ghost"}`}
          data-testid="pl-tool-bucket"><PaintBucket size={14} />Fill</button>
        <span className="w-px h-6 bg-black/20" />
        <button type="button" onClick={undo} className="nb-btn nb-btn-ghost text-xs" data-testid="pl-undo"><Undo2 size={14} />Undo</button>
        <button type="button" onClick={redo} className="nb-btn nb-btn-ghost text-xs" data-testid="pl-redo"><Redo2 size={14} />Redo</button>
        <button type="button" onClick={clearAll} className="nb-btn nb-btn-danger text-xs" data-testid="pl-clear"><Trash2 size={14} />Clear</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PALETTE.map((c) => (
          <button key={c} type="button" onClick={() => { setColor(c); setTool("brush"); }}
            className="w-7 h-7 rounded-full border-2 border-black shrink-0"
            style={{ background: c, outline: color === c ? "2px solid #111" : "none", outlineOffset: 2 }}
            data-testid={`pl-color-${c}`} title={c} />
        ))}
        <span className="w-px h-6 bg-black/20" />
        {SIZES.map((s) => (
          <button key={s} type="button" onClick={() => setSize(s)}
            className={`nb-btn text-xs ${size === s ? "" : "nb-btn-ghost"}`}
            data-testid={`pl-size-${s}`}>{s}px</button>
        ))}
      </div>
      <canvas ref={ref} width={720} height={480}
        className="w-full border-2 border-black rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
    </div>
  );
}
